/**
 * DOM helpers for scroll-anchor-based paging — used by chat (follow-up
 * comments) and the audit log to keep a chosen item visually stable while
 * older items are prepended.
 *
 * `itemAttribute` is the data attribute that marks paginatable rows in the
 * scroll container, e.g. `data-follow-up-message-id` or `data-audit-log-id`.
 */

export const getElementTopFromScrollBoxTop = (scrollElement: HTMLElement, element: HTMLElement) =>
  element.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top

export const getItemById = (root: HTMLElement, itemAttribute: string, id: string) => {
  return root.querySelector<HTMLElement>(`[${itemAttribute}="${CSS.escape(id)}"]`)
}

/** First item whose rect overlaps the scroll container's viewport. */
export const getFirstVisibleItem = (root: HTMLElement, itemAttribute: string) => {
  const rootRect = root.getBoundingClientRect()
  const elements = root.querySelectorAll<HTMLElement>(`[${itemAttribute}]`)
  for (const el of elements) {
    const rect = el.getBoundingClientRect()
    if (rect.bottom > rootRect.top && rect.top < rootRect.bottom) {
      return el
    }
  }
  return null
}

/**
 * First item that is visible AND positioned below the given reference element
 * (typically the "Show more" button). Falls back to {@link getFirstVisibleItem}
 * at call sites — this returns `null` when `after` is `null`.
 */
export const getFirstVisibleItemAfterElement = (
  root: HTMLElement,
  itemAttribute: string,
  after: HTMLElement | null
) => {
  if (!after) {
    return null
  }
  const rootRect = root.getBoundingClientRect()
  const afterRect = after.getBoundingClientRect()
  const boundary = Math.max(rootRect.top, afterRect.bottom)
  const elements = root.querySelectorAll<HTMLElement>(`[${itemAttribute}]`)
  for (const el of elements) {
    const rect = el.getBoundingClientRect()
    if (rect.bottom > boundary && rect.top < rootRect.bottom) {
      return el
    }
  }
  return null
}
